/**
 * Navigation Utilities
 *
 * Provides helpers for URL-based navigation and tab handling
 */

/**
 * Sales Order tab IDs
 */
export type SalesOrderTabId = 'overview' | 'lines' | 'stock-status' | 'backorders';

/**
 * Purchase Order tab IDs
 */
export type PurchaseOrderTabId = 'overview' | 'lines' | 'receiving';

/**
 * Parse tab parameter from URL to index
 */
export const parseTabParam = (tabParam: string | null, validTabs: string[]): number => {
  if (!tabParam) return 0;

  // Check if it's a numeric index
  const numericIndex = parseInt(tabParam, 10);
  if (!isNaN(numericIndex) && numericIndex >= 0 && numericIndex < validTabs.length) {
    return numericIndex;
  }

  // Check if it's a valid tab ID
  const tabIndex = validTabs.indexOf(tabParam);
  return tabIndex >= 0 ? tabIndex : 0;
};

/**
 * Get tab ID from index for sales orders
 */
export const getSalesOrderTabId = (index: number): SalesOrderTabId => {
  const tabs: SalesOrderTabId[] = ['overview', 'lines', 'stock-status', 'backorders'];
  return tabs[index] || 'overview';
};

/**
 * Get tab ID from index for purchase orders
 */
export const getPurchaseOrderTabId = (index: number): PurchaseOrderTabId => {
  const tabs: PurchaseOrderTabId[] = ['overview', 'lines', 'receiving'];
  return tabs[index] || 'overview';
};

/**
 * Build sales order detail URL
 */
export const buildSalesOrderDetailUrl = (
  orderId: string,
  tabId?: SalesOrderTabId
): string => {
  const baseUrl = `/orders/sales/${orderId}`;
  return tabId && tabId !== 'overview' ? `${baseUrl}?tab=${tabId}` : baseUrl;
};

/**
 * Build purchase order detail URL
 */
export const buildPurchaseOrderDetailUrl = (
  orderId: string,
  tabId?: PurchaseOrderTabId
): string => {
  const baseUrl = `/orders/purchase/${orderId}`;
  return tabId && tabId !== 'overview' ? `${baseUrl}?tab=${tabId}` : baseUrl;
};

/**
 * Build backorder detail URL
 */
export const buildBackorderDetailUrl = (backorderId: string): string => {
  return `/orders/backorders/${backorderId}`;
};
